import { MatchController } from "../../MatchController";
import { MatchManager } from "../../MatchManager";
import { PegsContainer } from "../../physics/PegsContainer";
import { BackgroundManager } from "../../BackgroundManager";
import { GUI } from "../../GUI/GUI";
import { HUD } from "../../HUD/HUD";
import { FieldBoundaries } from "../../physics/FieldBoundaries";
import { GoalKeeper } from "../../physics/pegs/GoalKeeper";
import { PreMatchLayer } from "./PreMatchLayer";
import { BallsManager } from "../../BallsManager";
import { GameVars } from "../../GameVars";
import { GameConstants } from "../../GameConstants";
import { GameManager } from "../../GameManager";
import { AudioManager, SoundTrack } from "../../AudioManager";

    export class FootballFieldState extends Phaser.State {

        public static currentInstance: FootballFieldState;

        public matchController: MatchController;
        public matchModel: MatchManager;
        public pegsContainer: PegsContainer;
        public backgroundManager: BackgroundManager;
        public gui: GUI;
        public hud: HUD;
        public fieldBoundaries: FieldBoundaries;
        public goalKeeper: GoalKeeper;
        public preMatchLayer: PreMatchLayer;
        public ballsManager: BallsManager;

        private blackLayer: Phaser.Sprite;
        private sceneContainer: Phaser.Group;
      
        public init(): void {

            FootballFieldState.currentInstance = this;

            MatchManager.init(this.game);
        }
        
        public create(): void {

            this.matchController = new MatchController(this.game);

            this.preMatchLayer = null;
           
            this.backgroundManager = new BackgroundManager( this.game );
            this.add.existing( this.backgroundManager );

            this.sceneContainer = this.add.group();
            this.sceneContainer.scale.y = GameVars.scaleY * GameVars.fieldCorrectionScaleY;
            this.sceneContainer.y = (1 - GameVars.scaleY * GameVars.fieldCorrectionScaleY) * GameConstants.GAME_HEIGHT / 2;

            // cargar los datos del nivel
            GameManager.initMatch();
        }

        public addPreMatchLayer(): void {
          
            this.preMatchLayer = new PreMatchLayer(this.game);
            this.add.existing( this.preMatchLayer);
        }

        public initialize(): void {

            this.matchController.init(this.backgroundManager.backgroundImage);

            this.game.physics.p2.reset();
            this.game.physics.p2.setBoundsToWorld( false, false, false , false);
            this.game.physics.p2.resume();

            // debe haber sido creado un state para que los materiales funcionen
            GameVars.getInstance().createMaterials();

            this.pegsContainer = new PegsContainer(this.game);
            this.sceneContainer.add(this.pegsContainer);
            this.goalKeeper = this.pegsContainer.goalKeeper;

            this.fieldBoundaries =  new FieldBoundaries(this.game);
            this.sceneContainer.add(this.fieldBoundaries );

            this.ballsManager = new BallsManager(this.game);
            this.sceneContainer.add(this.ballsManager);

            let goalImage = new Phaser.Image( this.game, 155, 960, "texture_atlas_1", "goal");
            this.sceneContainer.add(goalImage);

            this.hud = new HUD(this.game);
            this.add.existing(this.hud);

            this.gui = new GUI(this.game);
            this.add.existing(this.gui);

            // una vez se ha instanciado todas las cosas referentes al nivel
            if (this.preMatchLayer !== null) {
                this.preMatchLayer.onFootballSceneInitialized();
            } else {
                MatchManager.onMatchStart();
            }
        }

        public shutdown(): void {

            FootballFieldState.currentInstance = null;

            this.matchController.destroy();

            super.shutdown();
        }

        public onMatchStart(): void {

            this.ballsManager.start();
            this.hud.start();
            this.gui.start();

            if (GameVars.isTimedLevel) {
                AudioManager.getInstance().playMusic(SoundTrack.EXTRA_TIME);
            } else {
                AudioManager.getInstance().playMusic(SoundTrack.GAME_SOUNDTRACK);
            }
        }

        public kickOff(): void {

            this.ballsManager.kickOff();
            this.hud.updateBallsCounter();
            this.gui.onKickOff();
        }

        public shootBall( px: number, py: number, angle: number, powerKick: boolean, ballOnFire: boolean, isExtraBall: boolean): void {

            this.ballsManager.shootBall(px, py, angle, powerKick, ballOnFire, isExtraBall);
        }

        public onExtraBall( x: number, y: number, isBallOfFire: boolean): void {

            this.ballsManager.onExtraBall( x , y, isBallOfFire);
        }

        public onMagicPotion(): void {

            this.goalKeeper.shrink();
        }

        public onPowerUpFreezeGoalkeeperSelected(): void {

            this.hud.onPowerUpSelected( GameConstants.FREEZE_GOALKEEPER );
            this.goalKeeper.onFreezeGoalKeeper();
        }

        public onPowerUpExtraBallsSelected(): void {

            this.hud.onPowerUpSelected( GameConstants.EXTRA_BALLS );

            AudioManager.getInstance().playEffect("power_up");
        }

        public onPowerUpBallOfFireSelected(): void {

            this.hud.onPowerUpSelected( GameConstants.BALL_OF_FIRE );
            this.ballsManager.onBallOnFireSelected();

            AudioManager.getInstance().playEffect("power_up");
        }

        public onPowerUpVoodooSelected(): void {

            this.hud.onPowerUpSelected( GameConstants.VOODOO );
            this.backgroundManager.onVoodoo();
            this.pegsContainer.onVoodoo();

            AudioManager.getInstance().playEffect("power_up");
        }

        public onGoal(): void {

            this.backgroundManager.onGoal();
            this.hud.onGoal();
            this.goalKeeper.onGoal();
        }

        public onNoBallsInTheField (goalScored: boolean): void {

            // se muestra el sacador pasado un delay
            // para que salga cuando desaparece el cartel de gol
            if ( goalScored ) {
                this.game.time.events.add(1250, function(): void {
                    this.ballsManager.start();
                    this.hud.onBallsManagerReadyToKickOff();
                }, this);
            } else {
                this.ballsManager.start();
                this.hud.onBallsManagerReadyToKickOff();
            }

            this.goalKeeper.onNoBallsInTheField();

            this.gui.start();
            // informar al pegs container para que gestione la salida de streakers
            // antes de que salga el chutador inicial
            this.pegsContainer.onBallsManagerReadyToKickOff();
        }

        public onBallReturnedToStartingPoint(): void {

            this.hud.updateBallsCounter();
            this.ballsManager.start();
        }

        public onStreakerEntry(): void {

            this.hud.onStreakerEntry();
        }

        public onStreakerHit(): void {

            this.hud.onStrikerHit();
        }

        public pauseGame(): void {

            this.game.physics.p2.pause();

            this.ballsManager.onGamePaused();
            this.pegsContainer.onGamePaused();
        }

        public resumeGame(): void {

            this.game.physics.p2.resume();

            this.ballsManager.onGameResumed();
            this.pegsContainer.onGameResumed();
        }

        public onMatchEnd(): void {

            this.game.physics.p2.pause();

            this.gui.visible = false;

            this.hud.onMatchEnd();

            // dependiendo del resultado y tambien de si hay celebracion o no se pasa a la
            // pantalla de resultado final

            let delayNextScene: number;

            if (GameVars.matchJustUnlocked || GameVars.tournamentJustUnlocked) {

                delayNextScene = 5500;

                this.hud.celebrateMatchEnd();

                if (GameVars.isTimedLevel) {
                    AudioManager.getInstance().stopMusic(SoundTrack.EXTRA_TIME);
                } else {
                    AudioManager.getInstance().stopMusic(SoundTrack.GAME_SOUNDTRACK);
                }

                AudioManager.getInstance().playEffect("celebracion_victoria");
            } else {

                delayNextScene = 900;
            }

            this.game.time.events.add(delayNextScene, function(): void { 
                GameManager.showMatchResult();
            }, this);
        }
    }
